import * as React from 'react';
import { Popover, PopoverTrigger, PopoverContent } from './popover';
import { ChevronDown } from 'lucide-react';
import { DateRangeSelector } from './DateRangeSelector';
import { format } from 'date-fns';

// DateRange type
interface DateRange {
  from: Date | undefined;
  to?: Date | undefined;
}

// Preset options with logic (copy from DateRangeSelector)
const presetOptions: { label: string; getRange: () => DateRange }[] = [
  {
    label: 'Today',
    getRange: () => {
      const d = new Date();
      return { from: d, to: d };
    },
  },
  {
    label: 'Yesterday',
    getRange: () => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return { from: d, to: d };
    },
  },
  {
    label: 'This week (Sun - Today)',
    getRange: () => {
      const d = new Date();
      const from = new Date(d);
      from.setDate(d.getDate() - d.getDay());
      return { from, to: d };
    },
  },
  {
    label: 'Last week (Sun - Sat)',
    getRange: () => {
      const d = new Date();
      const to = new Date(d);
      to.setDate(d.getDate() - d.getDay() - 1);
      const from = new Date(to);
      from.setDate(to.getDate() - 6);
      return { from, to };
    },
  },
  {
    label: 'Last 7 days',
    getRange: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(to.getDate() - 6);
      return { from, to };
    },
  },
  {
    label: 'Last 28 days',
    getRange: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(to.getDate() - 27);
      return { from, to };
    },
  },
  {
    label: 'Last 30 days',
    getRange: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(to.getDate() - 29);
      return { from, to };
    },
  },
  {
    label: 'Last 90 days',
    getRange: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(to.getDate() - 89);
      return { from, to };
    },
  },
  {
    label: 'Last 12 months',
    getRange: () => {
      const to = new Date();
      const from = new Date();
      from.setFullYear(to.getFullYear() - 1);
      from.setDate(from.getDate() + 1);
      return { from, to };
    },
  },
];

function getPresetLabel(range: DateRange): string {
  for (const preset of presetOptions) {
    const presetRange = preset.getRange();
    if (
      range.from && range.to &&
      presetRange.from && presetRange.to &&
      range.from.toDateString() === presetRange.from.toDateString() &&
      range.to.toDateString() === presetRange.to.toDateString()
    ) {
      return preset.label;
    }
  }
  return 'Custom';
}

function formatRange(from?: Date, to?: Date) {
  if (!from || !to) return '';
  return `${format(from, 'dd MMM yyyy')} - ${format(to, 'dd MMM yyyy')}`;
}

export function DateRangeDropdownV2({
  value,
  onChange,
  className,
}: {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [pendingRange, setPendingRange] = React.useState<DateRange>(value);

  React.useEffect(() => {
    setPendingRange(value);
  }, [value]);

  const handleApply = () => {
    onChange(pendingRange);
    setOpen(false);
  };
  const handleCancel = () => {
    setPendingRange(value);
    setOpen(false);
  };

  const presetLabel = getPresetLabel(value);
  const rangeLabel = formatRange(value.from, value.to);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex items-center border rounded-lg px-2 py-2 bg-white shadow-sm gap-2 md:min-w-[320px] ${className ?? ''}`}
          style={{ justifyContent: 'space-between' }}
        >
          <span className="flex items-center gap-2">
            <span className="bg-gray-100 rounded-md px-2 md:px-3 py-1 text-xs text-gray-400 font-medium mr-2">
              {presetLabel}
            </span>
            <span className="text-xs md:text-sm font-semibold text-gray-500">
              {rangeLabel}
            </span>
          </span>
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="p-0 w-[95vw] max-w-[600px] md:w-auto">
        <DateRangeSelector
          value={pendingRange}
          onChange={setPendingRange}
          onApply={handleApply}
          onCancel={handleCancel}
        />
      </PopoverContent>
    </Popover>
  );
} 